import { ColumnDef, SortingState } from "@tanstack/react-table";
import cn from "classnames";
import { If } from "components";
import { useBoolean } from "hooks";
import { t } from "i18next";
import moment from "moment";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from "react";
import { Button, ItemsTable, Loader } from "ui";

import { useGetTransfer } from "../../apis";
import { ETransferItemState, ETransferStatus } from "../../constants";
import {
  formatTransferRef,
  getTransferActorName,
  getTransferDirectionZones,
} from "../../helpers";
import { TransferBoxRef, TransferItem } from "../../transfers.types";
import { CancelTransferModal } from "../CancelTransferModal";

import styles from "./TransferDetailView.module.scss";

interface TransferDetailViewProps {
  transferId: string;
}

const renderBox = (box?: TransferBoxRef | null) => {
  if (!box) {
    return <span className={styles.muted}>{t("—")}</span>;
  }

  const sub = [box.zone ? t(box.zone) : null, box.locationLabel]
    .filter(Boolean)
    .join(" · ");

  return (
    <div>
      <div className={styles.box_name}>{box.name || box.id}</div>
      {sub ? <div className={styles.box_sub}>{sub}</div> : null}
    </div>
  );
};

const TransferDetailView = ({ transferId }: TransferDetailViewProps) => {
  const { transfer, isLoading, refetch } = useGetTransfer(transferId);
  const [sorting, setSorting] = useState<SortingState>([]);
  const {
    value: isCancelOpen,
    setTrue: openCancel,
    setFalse: closeCancel,
  } = useBoolean(false);

  // On a cancelled transfer the items were put back into their source boxes.
  const isCancelled = transfer?.status === ETransferStatus.Cancelled;

  const columns: Array<ColumnDef<TransferItem>> = useMemo(
    () => [
      {
        header: t("Product"),
        enableSorting: false,
        size: 40,
        minSize: 40,
        cell: ({ row }) => {
          const barcode = row.original.barcode;
          const variant = barcode?.productVariant;
          const imageUrl =
            variant?.images?.[0]?.url ??
            variant?.preview?.url ??
            variant?.compressedPreview?.url;
          const name = barcode?.productVariant?.name ?? barcode?.value;
          const sub = [barcode?.sizeAttributeValue?.value, barcode?.value]
            .filter(Boolean)
            .join(" · ");
          return (
            <div className={styles.product}>
              {imageUrl ? (
                <img className={styles.product_thumb} src={imageUrl} alt="" />
              ) : (
                <div className={styles.product_thumb} />
              )}
              <div className={styles.product_text}>
                <span className={styles.product_name}>
                  {name ?? row.original.barcodeId}
                </span>
                {sub ? <span className={styles.product_sub}>{sub}</span> : null}
              </div>
            </div>
          );
        },
      },
      {
        header: t("From"),
        enableSorting: false,
        size: 24,
        minSize: 24,
        cell: ({ row }) => renderBox(row.original.sourceInventoryBox),
      },
      {
        header: t("To"),
        enableSorting: false,
        size: 24,
        minSize: 24,
        cell: ({ row }) => renderBox(row.original.destinationInventoryBox),
      },
      {
        header: t("State"),
        enableSorting: false,
        size: 12,
        minSize: 12,
        cell: ({ row }) => {
          const isPutBack =
            isCancelled && row.original.state !== ETransferItemState.Dispatched;
          return (
            <span
              className={cn(
                styles.itemState,
                styles[
                  `itemState__${isPutBack ? "Picked" : row.original.state}`
                ],
              )}
            >
              {isPutBack ? t("Put back") : t(row.original.state)}
            </span>
          );
        },
      },
    ],
    [isCancelled],
  );

  if (isLoading) {
    return <Loader />;
  }

  if (!transfer) {
    return <div className={styles.detail_note}>{t("Transfer not found")}</div>;
  }

  const { from, to } = getTransferDirectionZones(transfer.direction);
  const canCancel =
    transfer.status === ETransferStatus.Current ||
    transfer.status === ETransferStatus.Pending;

  return (
    <div className={styles.detail}>
      <div className={styles.detail_header}>
        <div className={styles.detail_titleBlock}>
          <span className={styles.detail_title}>
            {formatTransferRef(transfer.id)}
          </span>
          <span
            className={cn(styles.status, styles[`status__${transfer.status}`])}
          >
            {t(transfer.status)}
          </span>
          <span className={styles.detail_direction}>
            {t(from)}
            <span className={styles.detail_arrow}>→</span>
            {t(to)}
          </span>
        </div>

        <If condition={canCancel}>
          <Button
            variant="delete"
            title={t("Cancel transfer")}
            onClick={openCancel}
          />
        </If>
      </div>

      <div className={styles.detail_grid}>
        <div>
          <div className={styles.detail_field_label}>{t("Created by")}</div>
          <div className={styles.detail_field_value}>
            {getTransferActorName(transfer)}
          </div>
        </div>
        <div>
          <div className={styles.detail_field_label}>{t("Created")}</div>
          <div className={styles.detail_field_value}>
            {moment(transfer.createdAt).format("DD MMM YYYY, HH:mm")}
          </div>
        </div>
        <div>
          <div className={styles.detail_field_label}>{t("Items")}</div>
          <div className={styles.detail_field_value}>
            {transfer.itemsCount ?? transfer.items?.length ?? 0}
          </div>
        </div>
        <If condition={!!transfer.pickupValidatedAt}>
          <div>
            <div className={styles.detail_field_label}>
              {t("Pickup validated")}
            </div>
            <div className={styles.detail_field_value}>
              {moment(transfer.pickupValidatedAt).format("DD MMM YYYY, HH:mm")}
            </div>
          </div>
        </If>
        <If condition={!!transfer.closedAt}>
          <div>
            <div className={styles.detail_field_label}>{t("Closed")}</div>
            <div className={styles.detail_field_value}>
              {moment(transfer.closedAt).format("DD MMM YYYY, HH:mm")}
            </div>
          </div>
        </If>
        <If condition={!!transfer.cancelledAt}>
          <div>
            <div className={styles.detail_field_label}>{t("Cancelled")}</div>
            <div className={styles.detail_field_value}>
              {moment(transfer.cancelledAt).format("DD MMM YYYY, HH:mm")}
            </div>
          </div>
        </If>
      </div>

      <If condition={!!transfer.cancelNote}>
        <div className={styles.detail_note}>{transfer.cancelNote}</div>
      </If>

      <div className={styles.detail_sectionTitle}>{t("Items")}</div>

      <ItemsTable
        columns={columns}
        data={transfer.items ?? []}
        sorting={sorting}
        setSorting={setSorting}
        skeletonHeight={56}
        emptyListMessage="No items in this transfer"
      />

      <CancelTransferModal
        isOpen={isCancelOpen}
        transfer={transfer}
        onClose={closeCancel}
        refetch={refetch}
      />
    </div>
  );
};

export default TransferDetailView;
